import { Link } from '@tanstack/react-router';
import { Text } from '../../ui/index.js';
import { LEGAL } from '../../lib/legal.js';

/**
 * The foot of the website: who you are dealing with, how to reach them, and the two documents.
 *
 * This is the one piece of chrome in the app that exists for somebody who is not a player. A
 * store that takes money has to put its support address and its legal pages where a visitor
 * finds them without navigating — on the public site, not only behind a screen they reach from
 * the rail — and a landing page that keeps both in Settings is the most common reason a payment
 * account is sent back for changes. `/studio` and `/settings` still carry the same details; this
 * is what makes them reachable from the front door.
 *
 * Web only. In the desktop and Android apps a page footer is not a thing that exists — there is
 * no page to be at the foot of — and the same three facts are on the Legal card in Settings,
 * which is in the rail on every platform.
 *
 * Nothing here is wrapped in `Focusable`. These are real anchors, so a pointer and the Tab key
 * both reach them, and a controller has the Settings route for the same two documents. Adding
 * them to the spatial grid would put three dead ends under every shelf on every screen, which
 * is a worse trade than a footer a stick walks past. The address is printed rather than linked
 * for the reason the studio page gives: nothing in this app hands the screen to a mail client.
 */
export function SiteFooter() {
  return (
    <footer className="gb-footer">
      <div className="gb-footer__row">
        <Text size="1" color="gray" mono>
          {`Support — ${LEGAL.contactEmail}`}
        </Text>
        <span className="gb-footer__spacer" />
        <Link to="/terms" className="gb-footer__link">
          {'Terms of service'}
        </Link>
        <Link to="/privacy" className="gb-footer__link">
          {'Privacy policy'}
        </Link>
      </div>

      {/* The particulars, the same ones the studio page carries at its own foot. A sole
          proprietorship, said plainly: a footer that implied a company would be the first
          untrue line on the site. */}
      <Text size="1" color="gray" mono>
        {`${LEGAL.brand} is operated by ${LEGAL.entity}, ${LEGAL.address} · NIP ${LEGAL.nip} · REGON ${LEGAL.regon}`}
      </Text>

      {/* Named here because it is the name that appears on a card statement, and a footer is
          where somebody checks a charge they did not recognise. */}
      <Text size="1" color="gray" mono>
        {'Memberships are sold by Creem B.V., our merchant of record.'}
      </Text>
    </footer>
  );
}
